import { LOCAL_STORAGE_TYPES, APP_ROUTE, ONBOARDING } from '@/constants';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ClientContainer, UserContainer } from '@/providers';

import useLocalStorage from '@/hooks/localstorage';
import { useChangeBusiness } from '@/hooks/api/business';
import { useSetPermissions } from '@/hooks/api/user';
import {
  Navigate,
  useParams,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { useCallback, useEffect, useState } from 'react';
import { useSession } from '@/states/common';
import { useTranslation } from 'react-i18next';
import { useSwitchBusiness } from '@/hooks/use-business-switch';
import { BusinessSwitchLoader } from '@/components/ui/business-switch-loader';

interface Permission {
  clientId: string;
  businessId: string;
}

interface Business {
  id: string;
  name: string;
  logo?: string;
  access: {
    isPrimary: boolean;
  };
  clients: Array<{
    id: string;
  }>;
  // eslint-disable-next-line camelcase
  is_demo_account?: boolean;
}

interface WorkspaceStorage {
  businessId: string;
}

export function SwitchBusinessModal() {
  const { t } = useTranslation('workspace');
  const { state: userState } = UserContainer.useContainer();
  const { state: clientState } = ClientContainer.useContainer();
  const sessionQuery = useSession();
  const { get } = useLocalStorage();
  const { clientId: urlClientId } = useParams<{ clientId: string }>();
  const location = useLocation();
  const navigate = useNavigate();

  const [showSwitchBusinessModal, setShowSwitchBusinessModal] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSwitching, setIsSwitching] = useState(false);

  const handleCloseModal = useCallback(() => {
    setShowSwitchBusinessModal(false);
    setError(null);

    // When user chooses "Not Now", navigate them to their current business's workspace
    // This ensures the URL matches their actual business context
    if (clientState?.selectedClient?.id) {
      const currentRoute =
        location.pathname.split('/').slice(3).join('/') || APP_ROUTE.HOME;
      navigate(`/ws/${clientState.selectedClient.id}/${currentRoute}`);
    }
  }, [clientState?.selectedClient?.id, location.pathname, navigate]);

  const changeBusiness = useChangeBusiness();
  const setCurrentBusinessPermission = useSetPermissions();
  const switchBusiness = useSwitchBusiness();

  // Use UserContainer state as primary source, fall back to session data
  // This allows local updates (like new workspace creation) to take precedence
  // over potentially stale session data
  const sessionData = sessionQuery?.data;
  const businessOptions =
    sessionData?.businessOptions || userState?.businessOptions;
  const { onboarding: onboardingStatus } = userState?.user ?? {};

  const hasWorkspaceAccessWithCurrentBusiness = (
    clientId?: string,
    businessId?: string
  ): boolean => {
    if (!clientId || !businessId || !userState?.permissions) return false;

    const permissions = Array.isArray(userState.permissions)
      ? userState.permissions
      : [userState.permissions];

    return permissions.some((permission: Permission) => {
      return (
        permission.clientId === clientId && permission.businessId === businessId
      );
    });
  };

  const getBusinessWithClient = useCallback(
    (userData: any): Business | null => {
      const options = businessOptions || userData?.businessOptions;
      if (!options || !urlClientId) return null;

      return (
        options.find((business: Business) =>
          business.clients?.some(client => String(client.id) === urlClientId)
        ) || null
      );
    },
    [urlClientId, businessOptions]
  );

  const handleBusinessSwitch = async (business: Business) => {
    try {
      setError(null);
      setIsSwitching(true);

      await switchBusiness({
        business,
        currentPath: location.pathname,
        currentClientId: clientState?.selectedClient?.id,
      });

      // Close modal after successful switch
      setShowSwitchBusinessModal(false);
      setIsSwitching(false);
    } catch (errorState) {
      setIsSwitching(false);
      setError(
        t(
          'switch-business-error',
          'Failed to switch business. Please try again.'
        )
      );
    }
  };

  const hasValidClientInUrl = useCallback(() => {
    if (!urlClientId || !businessOptions) {
      return false;
    }

    return businessOptions.some((business: Business) =>
      business.clients?.some(client => String(client.id) === urlClientId)
    );
  }, [urlClientId, businessOptions]);

  useEffect(() => {
    const businessForCurrentClient = getBusinessWithClient(userState);
    if (
      businessForCurrentClient &&
      String(userState?.business?.id) !== String(businessForCurrentClient.id)
    ) {
      setShowSwitchBusinessModal(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userState?.businessOptions, clientState?.selectedClient?.id]);

  // Handle routing cases
  if (window.location.pathname === `/${APP_ROUTE.ONBOARDING}`) {
    return null;
  }

  if (onboardingStatus !== ONBOARDING.COMPLETE_STEP) {
    return <Navigate to={`/${APP_ROUTE.ONBOARDING}`} />;
  }

  // Redirect if user has an invalid/unauthorized client ID in the URL
  // Only redirect if urlClientId exists but is invalid (not when it's missing)
  // Wait for businessOptions to load before checking validity
  if (
    urlClientId &&
    businessOptions &&
    !hasValidClientInUrl() &&
    clientState?.selectedClient?.id
  ) {
    return (
      <Navigate
        to={`/ws/${clientState?.selectedClient?.id}/${APP_ROUTE.HOME}`}
      />
    );
  }

  const currentBusiness = getBusinessWithClient(userState);
  const workspaceStorage = get(
    LOCAL_STORAGE_TYPES.WORKSPACE
  ) as WorkspaceStorage | null;

  // Use URL client ID as source of truth
  const currentClientId = urlClientId || clientState?.selectedClient?.id;

  // Check if the business we're switching to is a demo business
  const isDemoBusinessSwitch = currentBusiness?.is_demo_account === true;

  // Render demo welcome content
  const renderDemoWelcome = () => (
    <div className="space-y-4 mb-4">
      <p className="text-gray-700">
        {t(
          'demo-welcome-message',
          "Welcome! You're about to explore a demo workspace with sample data."
        )}
      </p>
      <div className="flex items-start gap-3 bg-blue-50 border border-blue-100 rounded-lg p-4">
        <i className="ti ti-sparkles text-blue-600 text-lg mt-0.5" />
        <div className="flex-1">
          <p className="text-sm text-gray-700 mb-1">
            {t(
              'demo-welcome-features',
              'Try out all features with pre-populated data'
            )}
          </p>
          <p className="text-xs text-gray-600">
            {t(
              'demo-welcome-description',
              'Explore reports, analytics, and integrations without connecting your own accounts.'
            )}
          </p>
        </div>
      </div>
    </div>
  );

  // Render standard switch business content
  const renderStandardSwitch = () => (
    <p className="text-gray-600 mb-4">
      {t(
        'switch-business-message',
        'You need to switch business first to access this workspace'
      )}
    </p>
  );

  return (
    <>
      {isSwitching && <BusinessSwitchLoader />}
      <Dialog open={showSwitchBusinessModal} onOpenChange={handleCloseModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {isDemoBusinessSwitch
                ? t('switch-to-demo-workspace', 'Switch to Demo Workspace')
                : t('switch-business-title', 'Switch Business')}
            </DialogTitle>
          </DialogHeader>

          {error && <p className="text-red-600 mb-4">{error}</p>}

          {!hasWorkspaceAccessWithCurrentBusiness(
            currentClientId,
            userState?.business?.id
          ) && (
            <>
              {isDemoBusinessSwitch
                ? renderDemoWelcome()
                : renderStandardSwitch()}

              <div className="grid gap-4">
                {currentBusiness ? (
                  <div
                    key={currentBusiness.id}
                    className="flex items-center justify-between p-4 rounded-lg border hover:border-primary transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      {currentBusiness.logo ? (
                        <img
                          src={currentBusiness.logo}
                          alt={currentBusiness.name}
                          className="w-10 h-10 rounded-full object-cover"
                        />
                      ) : (
                        <div
                          className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            isDemoBusinessSwitch ? 'bg-blue-100' : 'bg-gray-100'
                          }`}
                        >
                          <span
                            className={`text-lg font-medium ${
                              isDemoBusinessSwitch
                                ? 'text-blue-600'
                                : 'text-gray-600'
                            }`}
                          >
                            {currentBusiness.name.charAt(0)}
                          </span>
                        </div>
                      )}

                      <div>
                        <h3 className="font-medium flex items-center gap-2">
                          {currentBusiness.name}
                          {isDemoBusinessSwitch && (
                            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded font-medium">
                              {t('demo-label', 'Demo')}
                            </span>
                          )}
                          {currentBusiness.access?.isPrimary &&
                            !isDemoBusinessSwitch && (
                              <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded">
                                {t('primary-label', 'Primary')}
                              </span>
                            )}
                        </h3>
                        <p className="text-sm text-gray-500">
                          {t('workspace-count', '{{count}} workspace', {
                            count: currentBusiness.clients?.length ?? 0,
                          })}
                          {(currentBusiness.clients?.length ?? 0) !== 1
                            ? 's'
                            : ''}
                        </p>
                      </div>
                    </div>

                    <Button
                      intent="primary"
                      disabled={
                        workspaceStorage?.businessId === currentBusiness.id ||
                        isSwitching
                      }
                      onClick={() => {
                        if (
                          workspaceStorage?.businessId !== currentBusiness.id
                        ) {
                          handleBusinessSwitch(currentBusiness);
                        }
                      }}
                    >
                      {(() => {
                        if (isSwitching) {
                          return t('switching', 'Switching...');
                        }
                        if (
                          workspaceStorage?.businessId === currentBusiness.id
                        ) {
                          return t('current-business', 'Current Business');
                        }
                        if (isDemoBusinessSwitch) {
                          return t('explore-demo', 'Explore Demo');
                        }
                        return t('switch-button', 'Switch');
                      })()}
                    </Button>
                  </div>
                ) : (
                  <p className="text-gray-600">
                    {t(
                      'no-business-available',
                      'No business available to switch to.'
                    )}
                  </p>
                )}
              </div>

              {/* Action buttons */}
              <div className="flex justify-end gap-3 mt-4 pt-4 border-t">
                <Button
                  intent="secondary"
                  variant="outline"
                  onClick={handleCloseModal}
                  disabled={isSwitching}
                >
                  {t('not-now', 'Not Now')}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
